import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import mqtt, { MqttClient } from "mqtt";
import { IWeatherData } from "../types/device";
import { devicePublishUrl } from "../constant";

interface DeviceDataWithTimestamp {
  data: IWeatherData;
  lastUpdated: string; // ISO timestamp
}

interface DeviceDataMap {
  [deviceId: string]: DeviceDataWithTimestamp;
}

interface MqttState {
  client: MqttClient | null;
  isConnected: boolean;
  isPublishConnected: boolean;
  deviceDataMap: DeviceDataMap;
  subscribedTopics: string[];
  connectMqtt: (url: string, options?: mqtt.IClientOptions) => void;
  disconnectMqtt: () => void;
  subscribeTopic: (topic: string | string[]) => void;
  unsubscribeTopic: (topic: string | string[]) => void;
  unsubscribeAll: () => void;
  publishMessage: (topic: string, payload: string | object) => boolean;
  reconnectPublishClient: () => void;
  setLatestDeviceData: (deviceId: string, data: IWeatherData) => void;
  getDeviceData: (deviceId: string) => DeviceDataWithTimestamp | null;
}

const RECONNECT_INTERVAL = 5000;

// Dedicated publish-only connection (osmoair-device). The main `client` is the
// read-only browser user and the broker ACL rejects its publishes, so the MQTT
// Publish test widget must send via this separate device-credentialled client.
let publishClient: MqttClient | null = null;
function getPublishClient(): MqttClient {
  if (publishClient) return publishClient;
  publishClient = mqtt.connect(devicePublishUrl, {
    reconnectPeriod: RECONNECT_INTERVAL,
  });
  publishClient.on("connect", () => {
    useMqttStore.setState({ isPublishConnected: true });
  });
  publishClient.on("reconnect", () => {
    console.log("MQTT device-publish client reconnecting...");
  });
  publishClient.on("close", () => {
    useMqttStore.setState({ isPublishConnected: false });
  });
  publishClient.on("offline", () => {
    useMqttStore.setState({ isPublishConnected: false });
  });
  publishClient.on("error", (err) => {
    console.error("MQTT device-publish client error:", err);
    useMqttStore.setState({ isPublishConnected: false });
  });
  return publishClient;
}
function endPublishClient() {
  if (publishClient) {
    publishClient.end(true);
    publishClient = null;
  }
  useMqttStore.setState({ isPublishConnected: false });
}

const useMqttStore = create<MqttState>()(
  devtools(
    persist(
      (set, get) => ({
        client: null,
        isConnected: false,
        isPublishConnected: false,
        deviceDataMap: {},
        subscribedTopics: [] as string[],

        connectMqtt: (url, options = {}) => {
          // Don't create multiple connections
          const existing = get().client;
          if (existing?.connected) return;
          if (existing) {
            existing.end(true);
          }

          const client = mqtt.connect(url, {
            reconnectPeriod: RECONNECT_INTERVAL,
            ...options,
          });

          client.on("connect", () => {
            console.log("Connected to MQTT broker");
            set({ isConnected: true, client });

            // Re-subscribe to previously subscribed topics on reconnect
            const { subscribedTopics } = get();
            if (subscribedTopics.length > 0) {
              client.subscribe(subscribedTopics, (error) => {
                if (error) {
                  console.error("Re-subscribe error:", error);
                }
              });
            }
          });

          client.on("message", (topic, message) => {
            try {
              const messageString: IWeatherData = JSON.parse(message.toString());
              const deviceId = topic.split("/")[1];
              get().setLatestDeviceData(deviceId, messageString);
            } catch (err) {
              console.error("Failed to parse MQTT message:", err);
            }
          });

          client.on("error", (err) => {
            console.error("MQTT error:", err);
            set({ isConnected: false });
          });

          client.on("close", () => {
            set({ isConnected: false });
          });

          client.on("reconnect", () => {
            console.log("MQTT reconnecting...");
          });

          set({ client });
        },

        disconnectMqtt: () => {
          const { client } = get();
          if (client) {
            client.end(true);
            set({ client: null, isConnected: false, subscribedTopics: [] });
          }
          endPublishClient();
        },

        subscribeTopic: (topic) => {
          const { client, subscribedTopics } = get();
          const topics = Array.isArray(topic) ? topic : [topic];
          // Filter out already subscribed topics
          const newTopics = topics.filter((t) => !subscribedTopics.includes(t));
          if (newTopics.length === 0) return;

          if (client?.connected) {
            client.subscribe(newTopics, (error) => {
              if (error) {
                console.error("Subscribe error:", error);
              }
            });
          }
          set({ subscribedTopics: [...subscribedTopics, ...newTopics] });
        },

        unsubscribeTopic: (topic) => {
          const { client, subscribedTopics } = get();
          const topics = Array.isArray(topic) ? topic : [topic];
          if (client?.connected) {
            client.unsubscribe(topics, (error) => {
              if (error) {
                console.error("Unsubscribe error:", error);
              }
            });
          }
          set({ subscribedTopics: subscribedTopics.filter((t) => !topics.includes(t)) });
        },

        unsubscribeAll: () => {
          const { client, subscribedTopics } = get();
          if (client?.connected && subscribedTopics.length > 0) {
            client.unsubscribe(subscribedTopics);
          }
          set({ subscribedTopics: [] });
        },

        publishMessage: (topic, payload) => {
          // Publish via the dedicated device client (osmoair-device), NOT the
          // read-only browser `client` — the broker ACL would silently drop the
          // latter. QoS 1 so delivery failures surface in the callback.
          const message =
            typeof payload === "string" ? payload : JSON.stringify(payload);
          const pc = getPublishClient();
          pc.publish(topic, message, { qos: 1 }, (err) => {
            if (err) console.error("MQTT device publish failed:", err);
          });
          return true;
        },

        reconnectPublishClient: () => {
          // If the publish client exists but dropped, ask mqtt.js to reconnect
          // immediately rather than waiting out the reconnect interval. If it
          // was fully torn down (or never created), establish a fresh one.
          if (publishClient) {
            if (!publishClient.connected) {
              publishClient.reconnect();
            }
          } else {
            getPublishClient();
          }
        },

        setLatestDeviceData: (deviceId, data) => {
          set((state) => {
            const newMap = {
              ...state.deviceDataMap,
              [deviceId]: {
                data,
                lastUpdated: new Date().toISOString(),
              },
            };
            // Prune oldest entries if over limit
            const keys = Object.keys(newMap);
            if (keys.length > 100) {
              delete newMap[keys[0]];
            }
            return { deviceDataMap: newMap };
          });
        },

        getDeviceData: (deviceId) => {
          const { deviceDataMap } = get();
          return deviceDataMap[deviceId] || null;
        },
      }),
      {
        name: "mqtt-storage",
        partialize: (state) => ({
          deviceDataMap: state.deviceDataMap,
        }),
      }
    )
  )
);

export default useMqttStore;
